<template>
    <v-dialog v-model="modelValue" max-width="620" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center">
                <v-icon icon="mdi-export" class="mr-2" />
                Export 場次資料
            </v-card-title>
            <v-divider />

            <v-card-text style="max-height: 480px">
                <template v-if="!hasNdjson">
                    <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                        請先透過「Load」或「Reload」從檔案 / 伺服器載入資料後再使用 Export。
                    </v-alert>
                </template>
                <template v-else-if="fights.length === 0">
                    <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
                        尚無可匯出的場次（需有對 Boss 造成傷害的紀錄）。
                    </v-alert>
                </template>
                <template v-else>
                    <!-- Filter -->
                    <v-text-field
                        v-model="filterText"
                        density="compact"
                        hide-details
                        clearable
                        placeholder="篩選名稱 / 種族…"
                        prepend-inner-icon="mdi-magnify"
                        class="mb-2"
                    />

                    <!-- 全選 / 全不選 + 計數 + Top/End -->
                    <div class="d-flex align-center mb-1" style="gap: 8px">
                        <v-btn size="x-small" variant="tonal" @click="selectAll">全選</v-btn>
                        <v-btn size="x-small" variant="tonal" @click="selectNone">全不選</v-btn>
                        <span class="text-caption text-disabled">
                            已選 {{ selectedKeys.size }} / {{ fights.length }} 場
                            <template v-if="filteredFights.length !== fights.length">
                                （顯示 {{ filteredFights.length }}）
                            </template>
                        </span>
                        <v-spacer />
                        <v-btn icon size="x-small" variant="text" title="Top" @click="scrollToTop">
                            <v-icon size="small">mdi-chevron-double-up</v-icon>
                        </v-btn>
                        <v-btn icon size="x-small" variant="text" title="End" @click="scrollToEnd">
                            <v-icon size="small">mdi-chevron-double-down</v-icon>
                        </v-btn>
                    </div>

                    <!-- 無搜尋結果 -->
                    <div v-if="filteredFights.length === 0" class="text-caption text-disabled text-center py-4">
                        無符合條件的場次
                    </div>

                    <!-- 捲動區域 -->
                    <div v-else ref="scrollEl" style="max-height: 340px; overflow-y: auto">
                        <v-list density="compact" lines="two">
                            <v-list-item
                                v-for="f in filteredFights"
                                :key="f.entityKey"
                                :value="f.entityKey"
                                class="px-1"
                            >
                                <template #prepend>
                                    <v-checkbox-btn
                                        :model-value="selectedKeys.has(f.entityKey)"
                                        @update:model-value="toggle(f.entityKey)"
                                        density="compact"
                                        class="mr-1"
                                    />
                                </template>
                                <v-list-item-title class="text-body-2">
                                    {{ f.displayName }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ fmtDateTime(f.startAt) }} ～ {{ fmtDateTime(f.endAt) }}
                                    <span class="ml-2">({{ fmtDuration(f.endAt - f.startAt) }})</span>
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </template>
            </v-card-text>

            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="modelValue = false">取消</v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    :disabled="!hasNdjson || selectedKeys.size === 0"
                    prepend-icon="mdi-download"
                    @click="doExport"
                >Export</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, inject, type Ref } from "vue";
import { ActorManager, EntityActor } from "@/eventActor";
import { prettyEntityName } from "@/lib/util";

export default defineComponent({
    name: "ExportDialog",
    props: {
        modelValue: { type: Boolean, required: true },
        rawNdjson:  { type: String,  required: true },
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const modelValue = computed({
            get: () => props.modelValue,
            set: (v) => emit("update:modelValue", v),
        });

        const actorManager = inject("actorManager") as Ref<ActorManager>;
        const showDate     = inject("showDate")     as Ref<boolean>;
        const raceNameMap  = inject("raceNameMap")  as Ref<Record<number, string>>;

        const hasNdjson = computed(() => props.rawNdjson.length > 0);

        type FightItem = {
            entityKey: string;
            displayName: string;
            raceId: number;
            startAt: number;
            endAt: number;
        };

        const fights = computed((): FightItem[] => {
            const result: FightItem[] = [];
            for (const [key, e] of Object.entries(actorManager.value.entityMap)) {
                const ea = e as EntityActor;
                if (ea.isPC || ea.ownerId || !ea.takeDamages.length) continue;
                let startAt = Infinity, endAt = -Infinity;
                for (const d of ea.takeDamages) {
                    if (d.At < startAt) startAt = d.At;
                    if (d.At > endAt)   endAt   = d.At;
                }
                if (!isFinite(startAt)) continue;
                const displayName =
                    prettyEntityName(ea, raceNameMap) ??
                    raceNameMap.value[ea.raceId] ??
                    `Race ${ea.raceId}`;
                result.push({ entityKey: key, displayName, raceId: ea.raceId, startAt, endAt });
            }
            return result.sort((a, b) => b.startAt - a.startAt);
        });

        // ── 篩選 ─────────────────────────────────────────────────────
        const filterText = ref("");

        const filteredFights = computed(() => {
            const q = filterText.value.trim().toLowerCase();
            if (!q) return fights.value;
            return fights.value.filter(f => {
                return f.displayName.toLowerCase().includes(q) || String(f.raceId).includes(q);
            });
        });

        // ── 選擇（作用在全部場次，不受篩選限制）────────────────────
        const selectedKeys = ref(new Set<string>());
        watch(fights, (list) => {
            selectedKeys.value = new Set(list.map((f) => f.entityKey));
        }, { immediate: true });

        const toggle = (key: string) => {
            const next = new Set(selectedKeys.value);
            if (next.has(key)) next.delete(key);
            else               next.add(key);
            selectedKeys.value = next;
        };

        // 全選 / 全不選 只作用在目前可見（篩選後）的項目
        const selectAll  = () => {
            const next = new Set(selectedKeys.value);
            filteredFights.value.forEach(f => next.add(f.entityKey));
            selectedKeys.value = next;
        };
        const selectNone = () => {
            const next = new Set(selectedKeys.value);
            filteredFights.value.forEach(f => next.delete(f.entityKey));
            selectedKeys.value = next;
        };

        // ── 格式化工具 ───────────────────────────────────────────────
        const fmtDateTime = (ts: number): string => {
            const d = new Date(ts * 1000);
            if (showDate.value) {
                return d.toLocaleString("zh-TW", {
                    month: "2-digit", day: "2-digit",
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                    hour12: false,
                });
            }
            return d.toLocaleTimeString("zh-TW", { hour12: false });
        };

        const fmtDuration = (secs: number): string => {
            const m = Math.floor(secs / 60);
            const s = Math.floor(secs % 60);
            return m > 0 ? `${m}m${s.toString().padStart(2, "0")}s` : `${s}s`;
        };

        // ── Export 邏輯 ──────────────────────────────────────────────
        const doExport = () => {
            const selected = fights.value.filter((f) => selectedKeys.value.has(f.entityKey));
            if (!selected.length) return;

            const ranges = selected.map((f) => ({ start: f.startAt, end: f.endAt }));

            const filtered: string[] = [];
            for (const line of props.rawNdjson.split("\n")) {
                const trimmed = line.trim();
                if (!trimmed) continue;
                try {
                    const ev = JSON.parse(trimmed) as { At?: number };
                    const at = ev.At;
                    if (at === undefined || ranges.some((r) => at >= r.start && at <= r.end)) {
                        filtered.push(trimmed);
                    }
                } catch {
                    // 略過無效行
                }
            }

            const earliest = selected.reduce((m, f) => Math.min(m, f.startAt), Infinity);
            const d = isFinite(earliest) ? new Date(earliest * 1000) : new Date();
            const pad = (n: number) => n.toString().padStart(2, "0");
            const datePart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const timePart = `${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
            const filename = `packet_log_${datePart}_${timePart}.ndjson`;

            const blob = new Blob([filtered.join("\n") + "\n"], { type: "application/x-ndjson" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);

            modelValue.value = false;
        };

        const scrollEl = ref<HTMLElement | null>(null);
        const scrollToTop = () => { if (scrollEl.value) scrollEl.value.scrollTop = 0; };
        const scrollToEnd = () => { if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight; };

        return {
            modelValue,
            hasNdjson,
            fights,
            filterText,
            filteredFights,
            raceNameMap,
            selectedKeys,
            toggle,
            selectAll,
            selectNone,
            fmtDateTime,
            fmtDuration,
            doExport,
            scrollEl,
            scrollToTop,
            scrollToEnd,
        };
    },
});
</script>
